import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { AuthContext } from "@/App";
import { X, ArrowDownToLine } from "lucide-react";
import { ProductGlyph } from "@/components/EcotileGlyph";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inN = (v) => Number(v || 0).toLocaleString("en-IN");

// Metallic-style sourcing hub: per tile line, list matching FG lots and let the
// user Reserve a chosen quantity from the lot they pick. Mirrors CheckStoresPanel.
export default function EcotileFgSourcing({ order, onClose, onChanged }) {
  const { user } = useContext(AuthContext);
  const canManage = user?.role === "Admin" || !!user?.permissions?.can_manage_ecotile_orders;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reserveFor, setReserveFor] = useState(null); // lot id being reserved
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/ecotile/orders/${order.id}/fg-sourcing`); setData(r.data); }
    catch (e) { toast.error("Could not load FG stock"); }
    finally { setLoading(false); }
  }, [order.id]);
  useEffect(() => { load(); }, [load]);

  const startReserve = (lot, line) => {
    setReserveFor(lot.id);
    const short = Math.max((line.needed || 0) - (line.reserved || 0), 0);
    const def = Math.min(lot.free || 0, short > 0 ? short : (lot.free || 0));
    setQty(def > 0 ? String(Math.round(def)) : "");
  };
  const doReserve = async (lot) => {
    const v = parseFloat(qty);
    if (!v || v <= 0) { toast.error("Enter a quantity"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/ecotile/fg/${lot.id}/reserve`, { order_id: order.id, qty: v });
      toast.success(`Reserved ${v} tiles from ${lot.location}`);
      setReserveFor(null); setQty(""); await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reserve"); }
    finally { setBusy(false); }
  };
  const release = async (lot, r) => {
    try {
      await axios.post(`${API}/ecotile/fg/${lot.id}/unreserve?reservation_id=${encodeURIComponent(r.id)}`);
      toast.success("Released"); await load(); onChanged?.();
    } catch (e) { toast.error("Could not release"); }
  };

  const lines = data?.lines || [];
  const totNeed = lines.reduce((s, l) => s + (l.needed || 0), 0);
  const totRes = lines.reduce((s, l) => s + (l.reserved || 0), 0);

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 overflow-auto" style={{ background: "rgba(0,0,0,.45)" }} onClick={onClose}>
      <div className="rl-frost rl-glass-edge w-full max-w-[640px] rounded-2xl border border-[hsl(var(--border))] shadow-2xl mt-10" onClick={(e) => e.stopPropagation()} data-testid="eco-fg-sourcing">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[hsl(var(--border))]">
          <div>
            <div className="font-bold text-[15px]">Source from FG stock</div>
            <div className="text-[12px] text-[hsl(var(--muted-foreground))]">{order.organisation_name}{order.seq_no ? ` · #${order.seq_no}` : ""} · pick the lots to hold against this order</div>
          </div>
          <button onClick={onClose} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-4 max-h-[62vh] overflow-auto">
          {loading ? (
            <div className="py-8 text-center text-[hsl(var(--muted-foreground))]">Loading FG stock…</div>
          ) : lines.length === 0 ? (
            <div className="py-8 text-center text-[hsl(var(--muted-foreground))]">This order has no tile line items to source from FG stock. Add line items on the order first.</div>
          ) : lines.map((line) => {
            const lots = (data.lots_by_code || {})[line.item_code] || [];
            const short = Math.max((line.needed || 0) - (line.reserved || 0), 0);
            return (
              <div key={line.item_code}>
                <div className="flex items-center gap-2 mb-1.5">
                  <ProductGlyph code={line.item_code} hex={(lots[0] || {}).hex} tex={(lots[0] || {}).texture} size={18} />
                  <div className="text-[12.5px] font-semibold truncate">{line.description || line.item_code}</div>
                  <span className="text-[10px] font-mono text-[hsl(var(--muted-foreground))]">{line.item_code}</span>
                  <span className={`ml-auto text-[11px] font-semibold whitespace-nowrap ${short > 0 ? "text-orange-600 dark:text-orange-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                    need {inN(line.needed)} · reserved {inN(line.reserved)}{short > 0 ? ` · short ${inN(short)}` : " ✓"}
                  </span>
                </div>
                <div className="rounded-lg border border-[hsl(var(--border))] divide-y divide-[hsl(var(--border))]/50">
                  {lots.length === 0 ? (
                    <div className="px-3 py-2 text-[11px] text-[hsl(var(--muted-foreground))]">No FG lots exist for this SKU yet — receive stock in the Stores portal.</div>
                  ) : lots.map((lot) => (
                    <div key={lot.id} className="px-3 py-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] font-medium w-[86px] shrink-0">{lot.location}</span>
                        <span className="text-[10px] font-mono text-[hsl(var(--muted-foreground))] w-[58px] shrink-0 truncate">{lot.batch_no || "—"}</span>
                        <span className="text-[11px] text-[hsl(var(--muted-foreground))] flex-1 min-w-0 truncate">on hand {inN(lot.qty_on_hand)} · reserved {inN(lot.qty_reserved)}</span>
                        <span className="text-[12px] font-semibold tabular-nums text-emerald-600 dark:text-emerald-400 whitespace-nowrap shrink-0">{inN(lot.free)} free</span>
                        {canManage && reserveFor !== lot.id && lot.free > 0 && (
                          <button type="button" onClick={() => startReserve(lot, line)} data-testid={`eco-reserve-${lot.id}`}
                            className="inline-flex items-center gap-1 text-[10.5px] font-medium px-1.5 py-0.5 rounded-md border border-blue-500/30 bg-blue-500/8 text-blue-700 dark:text-blue-300 hover:bg-blue-500/15 hover:border-blue-500/50 transition-colors shrink-0">
                            <ArrowDownToLine className="w-3 h-3" /> Reserve
                          </button>
                        )}
                        {canManage && reserveFor === lot.id && (
                          <span className="inline-flex items-center gap-1 shrink-0">
                            <input autoFocus value={qty} onChange={(e) => setQty(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doReserve(lot); } else if (e.key === "Escape") setReserveFor(null); }}
                              inputMode="decimal" className="w-16 h-6 px-1.5 rounded border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[12px] text-right" />
                            <button type="button" onClick={() => doReserve(lot)} disabled={busy} className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-blue-500 text-white disabled:opacity-50">Hold</button>
                            <button type="button" onClick={() => setReserveFor(null)} className="text-[hsl(var(--muted-foreground))] text-[14px] leading-none">×</button>
                          </span>
                        )}
                      </div>
                      {(lot.mine || []).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {lot.mine.map((r) => (
                            <span key={r.id} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-blue-500/30 bg-blue-500/8 text-blue-700 dark:text-blue-300">
                              held {inN(r.qty)}
                              {canManage && <button onClick={() => release(lot, r)} title="Release this hold" className="hover:text-red-500 text-[12px] leading-none">×</button>}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-[hsl(var(--border))]">
          <div className="text-[12px] text-[hsl(var(--muted-foreground))]">Total: need {inN(totNeed)} · reserved {inN(totRes)} tiles</div>
          <button onClick={onClose} className="h-8 px-4 rounded-full text-[13px] font-medium border border-[hsl(var(--border))]">Done</button>
        </div>
      </div>
    </div>
  );
}
